import { useEffect, useState } from "react";
import { ArrowRight, ArrowDown, MapPin, Phone, FileText, Video, Scissors, BarChart3, UserPlus, MessageSquare } from "lucide-react";
import { Logo } from "../components/site/Logo";
import { Footer } from "../components/site/Footer";
import { FadeUp, MaskedLines, Counter } from "../components/site/Motion";
import { ResultsDisclaimer } from "../components/site/CaseStudies";
import { PricingCards, PricingDisclosure } from "../components/site/Pricing";
import { QualificationForm } from "../components/form/QualificationForm";
import { useContent, usePageMeta, usePhone } from "../lib/content";
import { getSelectedPackage, scrollToId } from "../lib/api";
import { trackCta, trackOnce } from "../lib/analytics";

const STEPS = [
  ["We script your ads.", FileText], ["We film your content.", Video], ["We edit your videos.", Scissors],
  ["We manage your campaigns.", BarChart3], ["We generate customer inquiries.", UserPlus], ["We automate follow-up on qualifying packages.", MessageSquare],
];

function CompactCase({ cs, i }) {
  const stats = (Array.isArray(cs?.stats) ? cs.stats : []).slice(0, 2);
  return (
    <FadeUp delay={i * 0.1} className="relative overflow-hidden rounded-[26px] border border-white/[0.08] bg-panel" data-testid={`lp-case-${cs.id}`}>
      <img src={cs.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-30" style={{ objectPosition: cs.image_position || "center 20%" }} />
      <div className="absolute inset-0 bg-gradient-to-br from-panel via-panel/90 to-electric/20" />
      <div className="relative p-7 sm:p-9">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">{cs.figures_confirmed ? "Confirmed figures" : "Client-reported figures"}</p>
        <h3 className="mt-3 text-2xl font-semibold tracking-tight text-white">{cs.business}</h3>
        {stats.length === 2 && (
          <p className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            <Counter value={stats[0].value} prefix={stats[0].prefix} suffix={stats[0].suffix} />
            <ArrowRight className="h-6 w-6 text-cyan-300" />
            <span className="text-gradient"><Counter value={stats[1].value} prefix={stats[1].prefix} suffix={stats[1].suffix} /></span>
          </p>
        )}
        <p className="mt-2 text-sm text-slate-400">ad spend → reported revenue</p>
      </div>
    </FadeUp>
  );
}

export default function GetStarted() {
  const { content } = useContent();
  const phone = usePhone();
  const [selected, setSelected] = useState(getSelectedPackage());
  usePageMeta("Get More Leads | Done-For-You Meta Ads — Rivanta Labs", "See if Rivanta Labs is a good fit. Fully done-for-you Facebook & Instagram advertising for service businesses in Billings, Montana and beyond.");
  useEffect(() => { trackOnce("ViewContent", { content_name: "get-started" }, "lp"); }, []);
  useEffect(() => {
    const on = (e) => setSelected(e.detail);
    window.addEventListener("rivanta:package", on);
    return () => window.removeEventListener("rivanta:package", on);
  }, []);
  const toForm = (label) => { trackCta(label, "landing"); scrollToId("apply"); };
  const cases = Array.isArray(content?.case_studies) ? content.case_studies : [];

  return (
    <div className="relative">
      <header className="absolute inset-x-0 top-0 z-40">
        <div className="container-x flex h-[68px] items-center justify-between">
          <Logo testId="lp-logo-link" />
          <a href={`tel:${phone.tel}`} data-testid="lp-header-phone-link" className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-white hover:border-white/30">
            <Phone className="h-4 w-4 text-cyan-300" /> <span className="hidden sm:inline">Call or text</span> {phone.display}
          </a>
        </div>
      </header>
      <main>
        <section className="noise relative isolate overflow-hidden pb-20 pt-32 sm:pt-40 lg:pb-28" data-testid="lp-hero">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -right-40 -top-20 h-[640px] w-[640px] animate-drift rounded-full bg-electric/25 blur-[140px]" />
            <div className="absolute inset-0 grid-lines [mask-image:radial-gradient(ellipse_at_70%_10%,#000_10%,transparent_65%)]" />
          </div>
          <div className="container-x grid items-center gap-12 lg:grid-cols-[1.3fr_1fr]">
            <div>
              <p className="eyebrow mb-6">Done-for-you Meta advertising</p>
              <MaskedLines lines={["We Handle Your Ads.", <>You Handle the <span className="font-serif italic font-normal text-gradient">Customers.</span></>]}
                className="text-[2.5rem] leading-[1] sm:text-6xl lg:text-[4.6rem] font-semibold tracking-[-0.045em] text-white" />
              <FadeUp delay={0.4}>
                <p className="mt-7 text-lg font-medium text-white">Fully done-for-you Facebook and Instagram advertising.</p>
                <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-400">We script, film, edit, launch, and manage your campaigns so you can focus on running your business.</p>
              </FadeUp>
              <FadeUp delay={0.55} className="mt-9">
                <button onClick={() => toForm("See If We're a Good Fit")} data-testid="lp-hero-cta-btn" className="btn-primary group !px-7 !py-4 !text-base">
                  See If We're a Good Fit <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                </button>
                <p className="mt-6 flex items-start gap-2 text-sm text-slate-400"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" /> Based in Billings, Montana. Helping service-based businesses grow.</p>
              </FadeUp>
            </div>
            <FadeUp delay={0.3} className="relative mx-auto grid w-full max-w-md grid-cols-2 gap-3">
              <div className="self-start overflow-hidden rounded-3xl border border-white/10"><img src="/media/skyhigh-card.jpg" alt="Sky High Window Washing ad we filmed" className="aspect-[3/4] w-full object-cover object-top" /></div>
              <div className="mt-10 overflow-hidden rounded-3xl border border-white/10"><img src="/media/masonry-card.jpg" alt="Specialty Masonry Repair ad we filmed" className="aspect-[3/4] w-full object-cover object-top" /></div>
            </FadeUp>
          </div>
        </section>

        <section className="border-t border-white/[0.06] bg-char py-20 lg:py-28" data-testid="lp-results">
          <div className="container-x">
            <MaskedLines as="h2" inView lines={["Here's What We've Done", "for Other Businesses."]} className="text-3xl sm:text-5xl font-semibold tracking-[-0.035em] text-white" />
            <div className="mt-12 grid gap-5 md:grid-cols-2">{cases.map((cs, i) => <CompactCase key={cs.id || i} cs={cs} i={i} />)}</div>
            <ResultsDisclaimer text={content?.results_disclaimer} />
            <button onClick={() => toForm("Let's Talk About Your Business")} data-testid="lp-results-cta-btn" className="btn-primary group mt-8">
              Let's Talk About Your Business <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </section>

        <section className="py-20 lg:py-28" data-testid="lp-how">
          <div className="container-x">
            <MaskedLines as="h2" inView lines={["Here's How It Works."]} className="text-3xl sm:text-5xl font-semibold tracking-[-0.035em] text-white" />
            <ol className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
              {STEPS.map(([t, I], i) => (
                <FadeUp as="li" key={t} delay={i * 0.07} className="relative rounded-2xl border border-white/[0.08] bg-panel/60 p-5">
                  <div className="flex items-center justify-between"><I className="h-5 w-5 text-cyan-300" /><span className="font-mono text-xs text-slate-500">{i + 1}</span></div>
                  <p className="mt-6 text-[15px] font-medium leading-snug text-white">{t}</p>
                  {i < 5 && <span className="absolute -right-2 top-1/2 z-10 hidden h-4 w-4 -translate-y-1/2 rotate-45 border-r border-t border-electric/60 bg-ink lg:block" />}
                </FadeUp>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-t border-white/[0.06] bg-char py-20 lg:py-28" data-testid="lp-packages">
          <div className="container-x">
            <MaskedLines as="h2" inView lines={["Find the Right Package", "for Your Business."]} className="text-3xl sm:text-5xl font-semibold tracking-[-0.035em] text-white" />
            <div className="mt-12"><PricingCards packages={content?.packages} selected={selected} onSelect={() => setTimeout(() => scrollToId("apply"), 150)} /></div>
            <PricingDisclosure text={content?.ad_spend_disclosure} />
          </div>
        </section>

        <section id="apply" className="relative scroll-mt-20 py-20 lg:py-28" data-testid="lp-apply">
          <div className="pointer-events-none absolute left-1/2 top-1/3 -z-10 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-electric/15 blur-[140px]" />
          <div className="container-x grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="eyebrow">Qualification · 2 minutes</p>
              <h2 className="mt-5 text-3xl sm:text-5xl font-semibold tracking-[-0.035em] text-white">Let's see if we're a good fit.</h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">Answer a few quick questions. We'll review your answers and reach out using the contact details you provide.</p>
              <p className="mt-8 text-sm text-slate-400">Prefer to talk? <a href={`tel:${phone.tel}`} className="font-semibold text-white underline-offset-4 hover:underline" data-testid="lp-apply-phone-link">Call or text {phone.display}</a></p>
            </div>
            <QualificationForm />
          </div>
        </section>
      </main>
      <Footer minimal />
    </div>
  );
}
